import { useState } from 'react';
import { Quote, ChevronDown, ChevronUp } from 'lucide-react';
import { useReveal } from '../../hooks/useReveal';

const TESTIMONIALS = [
  {
    id: 'testimonial-lymarie',
    name: 'Lymarie Pérez',
    role: 'Madre de JFP · 8vo Grado',
    highlight: 'Rise & Shine logró que el aprendizaje dejara de ser una carga y se convirtiera en una experiencia significativa para nuestro hijo.',
    fullText: 'Hace un año atrás, enfrenté la difícil realidad de reconocer y aceptar que el sistema educativo tradicional estaba generando una ansiedad creciente en nuestro hijo.\n\nLa carga de exámenes, las tareas interminables y la presión académica nos llevó a cuestionar el enfoque educativo y contemplar alternativas.\n\nTras un proceso de reflexión, descubrimos Rise & Shine Learning Zone. Allí encontramos un modelo educativo más práctico, flexible y centrado en el estudiante.\n\nComo resultado, el estrés académico dejó de ser un problema y la dinámica familiar mejoró significativamente.\n\nHoy el aprendizaje es un proceso donde nuestro hijo disfruta explorar, investigar y prepararse para la vida.'
  },
  {
    id: 'testimonial-pamela',
    name: 'Dra. Pamela Zambrana, Psy.D',
    role: 'Madre de Lucas González Zambrana',
    highlight: 'Melissa y su equipo transformaron nuestro miedo en confianza y seguridad.',
    fullText: 'Lucas es un niño dentro del espectro autista, y uno de mis mayores temores era su adaptación escolar.\n\nEn Rise & Shine no solo entendieron sus necesidades, sino que le brindaron un espacio seguro, libre de ansiedad y lleno de apoyo.\n\nSu enfoque individualizado permitió que aprendiera a su propio ritmo, respetando su proceso.\n\nHoy Lucas es un niño confiado, amoroso y seguro dentro del ambiente escolar.\n\nSiempre estaremos agradecidos por la vocación y compromiso de Melissa y su equipo.'
  },
  {
    id: 'testimonial-ivanielys',
    name: 'Familia de Ivanielys',
    role: 'Padres',
    highlight: 'Atrévanse al cambio… aquí encontramos crecimiento, apoyo y esperanza.',
    fullText: 'Cuando nos dijeron que teníamos que cambiar a nuestra hija de colegio, sentimos mucho miedo.\n\nRise & Shine ha sido un cambio lleno de emociones y crecimiento.\n\nNuestra hija se adaptó rápidamente gracias al apoyo constante del equipo.\n\nNunca nos soltaron y seguimos viendo una gran mejoría, en un ambiente regular e inclusivo.'
  }
];

const TestimonialCard = ({ testimonial, index }) => {
  const [expanded, setExpanded] = useState(false);
  const ref = useReveal();
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${index * 90}ms` }}
      className="reveal relative bg-white p-8 md:p-10 pt-12 rounded-3xl shadow-[0_10px_40px_-10px_rgba(67,182,184,0.18)] border border-[#43b6b8]/10 flex flex-col h-full text-center hover:shadow-[0_20px_50px_-12px_rgba(67,182,184,0.28)] hover:-translate-y-1 hover:scale-[1.02] transition-all duration-300"
      data-testid={`testimonial-card-${testimonial.id}`}
    >
      {/* Floating quote badge */}
      <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-[#d3b163] rounded-2xl w-14 h-14 flex items-center justify-center shadow-md">
        <Quote className="w-7 h-7 text-white" fill="white" />
      </div>

      <p className="text-xl md:text-[22px] font-medium text-stone-900 mb-6 leading-snug italic">
        "{testimonial.highlight}"
      </p>

      <div className={`overflow-hidden transition-all duration-500 ease-in-out text-left ${expanded ? 'max-h-[1200px] opacity-100 mb-4' : 'max-h-0 opacity-0'}`}>
        <div className="text-base text-stone-700 leading-relaxed whitespace-pre-line border-l-2 border-[#d3b163]/40 pl-4 mt-2">
          {testimonial.fullText}
        </div>
      </div>

      <button
        onClick={() => setExpanded(!expanded)}
        className="inline-flex items-center justify-center gap-1.5 text-[#43b6b8] hover:text-[#359a9c] font-semibold text-sm transition-colors mb-8 mx-auto"
        data-testid={`read-more-${testimonial.id}`}
      >
        {expanded ? (<>Leer menos <ChevronUp className="w-4 h-4" /></>) : (<>Leer más <ChevronDown className="w-4 h-4" /></>)}
      </button>

      <div className="mt-auto pt-6 border-t border-[#d3b163]/20">
        <h4 className="font-semibold text-stone-900 text-lg leading-tight">{testimonial.name}</h4>
        <p className="text-sm text-stone-500 mt-1">{testimonial.role}</p>
      </div>
    </div>
  );
};

export const HomeTestimonials = () => {
  const headerRef = useReveal();
  return (
    <section className="py-24 md:py-32 bg-gradient-to-b from-[#FDFBF7] to-[#F3F0EA]" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <div ref={headerRef} className="reveal text-center mb-20 max-w-2xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#d3b163] mb-5 block">Testimonios de Padres</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-stone-900 mb-5 leading-tight">
            Historias reales de familias que confían en nosotros
          </h2>
          <div className="w-16 h-[3px] bg-[#d3b163] mx-auto mb-6 rounded-full"></div>
          <p className="text-base text-stone-600 leading-relaxed">
            Transformaciones, crecimiento y momentos significativos compartidos por las familias de Rise & Shine.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10 pt-6">
          {TESTIMONIALS.map((t, i) => <TestimonialCard key={t.id} testimonial={t} index={i} />)}
        </div>
      </div>
    </section>
  );
};
